import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import type { StudentPayload } from "./reportTypes";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

interface Version { id: string; generated_at: string; payload: StudentPayload; }
export default function ReportHistory({ learnerId, refresh, onSelect }: {
  learnerId: string; refresh: number; onSelect: (p: StudentPayload, id: string) => void;
}) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    void supabase.from("learner_reports").select("id, generated_at, payload").eq("learner_id", learnerId)
      .eq("audience", "student").order("generated_at", { ascending: false }).limit(100)
      .then(({ data, error }) => { if (live) { setError(!!error); setVersions((data ?? []) as unknown as Version[]); } });
    return () => { live = false; };
  }, [learnerId, refresh]);
  const chart = [...versions].reverse().filter(v => v.payload.presentation_snapshot?.roadmap_state).map(v => ({
    date: new Date(v.generated_at).toLocaleDateString(),
    steps: v.payload.presentation_snapshot?.roadmap_state?.progress.length ?? null,
  }));
  return <section className="mb-6 space-y-3 print:hidden" aria-label="Report history">
    <h2 className="font-display text-xl font-semibold">Your journey over time</h2>
    {error && <p role="status" className="text-sm text-muted-foreground">Saved versions couldn’t load. Your open report is still available.</p>}
    <div className="flex flex-wrap gap-2">{versions.map((v, i) => <Button key={v.id} variant="outline" className="h-12"
      onClick={() => onSelect(v.payload, v.id)}>
      Version {versions.length - i} · {new Date(v.generated_at).toLocaleString()}{v.payload.status.status !== "ready" ? " · Partial" : ""}
    </Button>)}</div>
    {chart.length > 1 ? <>
      <p className="text-sm text-muted-foreground">Recorded roadmap steps, not a measure of your potential.</p>
      <div className="h-48 w-full" role="img" aria-label={chart.map(p => `${p.date}: ${p.steps} steps`).join("; ")}>
        <ResponsiveContainer width="100%" height="100%"><LineChart data={chart}>
          <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" /><YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
          <Tooltip /><Line dataKey="steps" name="Recorded steps" stroke="hsl(var(--primary))" strokeWidth={2} connectNulls={false} isAnimationActive={false} />
        </LineChart></ResponsiveContainer>
      </div>
    </> : <p className="text-sm text-muted-foreground">Progress appears here as complete versions are saved.</p>}
  </section>;
}